// ============================================================================
// IDENTIFIANTS DÉTERMINISTES DES DONNÉES MAÎTRES — comment une clé MÉTIER devient une clé TECHNIQUE.
//
// POURQUOI DANS LE CONTRAT, et pas chez celui qui écrit. Plusieurs voies créent les mêmes entités :
// le PRODUCTEUR (back-office) quand un catalogue fournisseur arrive, la REPRISE (Console) quand on
// convertit une ancienne base, et demain une caisse hors ligne. Si chacune frappe ses identifiants à
// sa façon, la reprise crée la famille « PANTALON » sous un identifiant, le premier catalogue EDI en
// crée une SECONDE sous un autre, et le catalogue se dédouble — sans qu'aucune erreur ne le signale,
// puisque les deux écritures réussissent. La règle doit donc être UNE, et partagée.
//
// ⚠️ MODULE PUR ET RN-SAFE : il est consommé par la tablette (React Native) autant que par les
// serveurs. Aucun `node:crypto`, aucune dépendance — d'où l'empreinte maison ci-dessous.
//
// ⚠️ Ces fonctions ne servent QU'À CRÉER. Une entité déjà en base garde son identifiant d'origine,
// quel qu'il soit : c'est la recherche par clé métier qui fait converger les sources, pas la frappe.
// Réf. CADRAGE-Producteur-MasterData §3.A.
// ============================================================================

/**
 * Empreinte 64 bits d'une chaîne, en 16 caractères hexadécimaux — DÉTERMINISTE et sans dépendance.
 *
 * ⚠️ Deux fonctions de hachage indépendantes (FNV-1a et djb2) concaténées, plutôt qu'une seule sur
 * 32 bits : sur quelques milliers de coloris, 32 bits donneraient déjà un risque de collision de
 * l'ordre du pour-cent — et une collision ici, ce sont deux coloris distincts qui deviennent le même.
 * ⚠️ Volontairement NON cryptographique : on cherche la stabilité et l'absence de collision, pas la
 * résistance à un adversaire. Un `sha256` obligerait à `node:crypto`, indisponible sur la tablette.
 */
export function shortHash(value: string): string {
  let fnv = 0x811c9dc5;
  let djb = 5381;
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    fnv = Math.imul(fnv ^ c, 0x01000193) >>> 0;
    djb = ((Math.imul(djb, 33) >>> 0) + c) >>> 0;
  }
  return fnv.toString(16).padStart(8, '0') + djb.toString(16).padStart(8, '0');
}

/**
 * FORME NORMALISÉE d'un code métier — c'est ELLE qui fait l'identité, jamais le texte brut.
 *
 * ⚠️ POURQUOI. Une même famille arrive écrite `PANTALON` par une reprise, `Pantalon` par un catalogue
 * fournisseur, `pantalon ` par un fichier Excel. Comparer les textes bruts fait croire à trois entités
 * différentes : la deuxième source croit CRÉER, et réapplique les valeurs de création (ordre d'affichage
 * remis à zéro, libellé réécrit) sur une ligne qui existait déjà. Ce n'est pas un doublon — c'est pire,
 * parce qu'un doublon se voit dans une liste et qu'un écrasement ne se voit nulle part.
 *
 * ⚠️ On normalise la FORME, jamais le CONTENU : `NAVY`, `navy` et ` NAVY ` sont le même coloris ;
 * `NAVY` et `NAVY BLUE` restent deux coloris distincts, et c'est l'outil d'harmonisation qui décidera
 * de les regrouper — pas une règle appliquée à l'aveugle à l'import.
 */
export const normalizeCode = (v: string): string =>
  v.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim().replace(/ +/g, '-');

/**
 * LA MÊME NORMALISATION, EN SQL — pour comparer une colonne à ce que `normalizeCode` a produit.
 *
 * ⚠️ DÉCLARÉE ICI, À CÔTÉ DE SA JUMELLE TypeScript, et nulle part ailleurs : le back-office l'emploie
 * pour ses `code_norm`, la migration magasin v28 pour frapper les identifiants de `size`. Deux copies
 * « à peu près pareilles » auraient fini par diverger sur un accent, et une valeur écrite par l'une ne
 * se serait plus retrouvée par l'autre.
 * ⚠️ CE QU'ELLE NE COUVRE PAS, ET C'EST DIT : `translate` ne connaît que les accents du français ;
 * un macron ou un tilde hors de cette liste est retiré côté SQL et RÉDUIT côté TypeScript. Les codes
 * de référentiel et les valeurs de taille n'en portent pas ; un test tient l'équivalence sur ce
 * qu'ils portent.
 */
export const normalizeCodeSql = (column: string): string =>
  `regexp_replace(btrim(regexp_replace(${foldAccentsSql(`btrim(${column})`)}, '[^a-z0-9]+', ' ', 'g')), ' +', '-', 'g')`;

/**
 * Le PLIAGE seul, en SQL : minuscules, accents du français dépliés — pour comparer une colonne à un
 * terme de recherche plié par `searchNorm` (@orgazone/name-format).
 * ⚠️ MÊME LISTE que `normalizeCodeSql`, qui l'appelle : deux listes de `translate` finiraient par
 *    diverger sur un accent, et une recherche ne trouverait plus ce que l'autre côté écrit.
 */
export const foldAccentsSql = (column: string): string =>
  `translate(lower(${column}), 'àáâãäçèéêëìíîïñòóôõöùúûüýÿ', 'aaaaaceeeeiiiinooooouuuuyy')`;

/** Fragment d'identifiant lisible tiré d'un code libre — la forme normalisée, tronquée. */
const slug = (v: string): string => normalizeCode(v).slice(0, 40).replace(/-+$/, '');

/** `slug`, en SQL — la même troncature, le même retrait des tirets de queue. */
export const slugSql = (column: string): string =>
  `regexp_replace(left(${normalizeCodeSql(column)}, 40), '-+$', '')`;

/**
 * Identifiants techniques des entités de données maîtres, par clé métier.
 *
 * Deux familles de règles, et la différence tient à la nature du code :
 *  · code CONTRÔLÉ (article, rayon, famille, moyen de paiement…) → identifiant LISIBLE, dérivé du code.
 *    On peut lire `fam-pantalon` dans un journal et savoir de quoi il s'agit.
 *  · code SUBI (coloris livré par un fournisseur : `20380-NAVY WHITE BICOLOR STRIP`) → slug tronqué +
 *    empreinte. Le slug garde la lisibilité, l'empreinte garantit que deux codes voisins ne se
 *    confondent jamais — y compris quand la troncature les rend identiques.
 */
export const masterDataId = {
  article: (gencode: string): string => `art-${gencode.trim()}`,
  sector: (code: string): string => `sec-${slug(code)}`,
  family: (code: string): string => `fam-${slug(code)}`,
  // Codes CONTRÔLÉS (une poignée de valeurs, saisies ou reprises) → identifiant LISIBLE : on lit
  // `mar-tommy-hilfiger` dans un journal et on sait de quoi il s'agit.
  supplier: (code: string): string => `four-${slug(code)}`,
  brand: (code: string): string => `mar-${slug(code)}`,
  subFamily: (code: string): string => `sfa-${slug(code)}`,
  collection: (code: string): string => `col2-${slug(code)}`,
  /**
   * ⚠️ L'EMPREINTE PORTE SUR LA FORME NORMALISÉE, JAMAIS SUR LE TEXTE BRUT. Hachée sur le brut, elle
   * faisait dépendre l'identité d'une ORTHOGRAPHE : `Navy`, `NAVY` et `navy` — le même coloris —
   * donnaient trois identifiants. Le coloris était le SEUL cas discordant du schéma, toutes les autres
   * entités se frappant sur un `slug`, qui normalise. Le dépôt l'a payé une fois (« 159 Light Blue
   * Wash » en deux identifiants, 96 articles au coloris pendant) et s'en est protégé par un index de
   * reprise — une parade qui rendait la reprise cohérente avec elle-même et laissait la cause intacte.
   * ⚠️ L'EMPREINTE RESTE, ELLE, ET ELLE SERT : `slug` tronque à 40 caractères, donc deux codes
   * fournisseur longs partageant leur début (`20380-NAVY WHITE BICOLOR…`) s'écraseraient sans elle.
   * On garde la désambiguïsation, on retire la dépendance à l'orthographe.
   */
  color: (code: string): string => `col-${slug(code)}-${shortHash(normalizeCode(code))}`,
  paymentMethod: (code: string): string => `pm-${slug(code)}`,
  justification: (code: string): string => `jst-${slug(code)}`,
  referenceList: (kind: string, code: string): string => `ref-${slug(kind)}-${slug(code)}`,
  measurementUnit: (code: string): string => `mu-${slug(code)}`,
  /** Cible de promotion : l'identité EST le critère (promotion + colonnes), d'où une empreinte du tuple.
   *  Deux imports de la même opération commerciale convergent donc sur la même ligne, au lieu d'empiler
   *  des cibles en double qui élargiraient le périmètre de la remise à chaque passage. */
  promotionTarget: (businessKey: string): string => `pt-${shortHash(businessKey)}`,
  /** Grille de tailles : code CONTRÔLÉ (le back-office la nomme) → identifiant lisible. */
  sizeGrid: (name: string): string => `sg-${slug(name)}`,
  /**
   * Une VALEUR de grille : identité = (grille, valeur normalisée) → `sz-sg-lettres-m`.
   * ⚠️ L'IDENTIFIANT DE LA GRILLE ENTRE TEL QUEL, sans être re-normalisé : la migration v28 frappe le
   * même identifiant en SQL (`'sz-' || g.id || '-' || slugSql(value)`), et ne peut pas re-normaliser
   * une clé déjà frappée sans risquer de la tronquer autrement. Les deux formules doivent rendre la
   * même chaîne, sinon le producteur croit créer une valeur que la migration a déjà posée (Forme A).
   */
  size: (gridId: string, value: string): string => `sz-${gridId}-${slug(value)}`,
  /** Lien famille → grille : identité = le couple, déjà fait d'identifiants — on le lit tel quel dans un journal. */
  familySizeGrid: (familyId: string, gridId: string): string => `fsg-${familyId}-${gridId}`,
  /** Photo d'article : identité = (produit, coloris, rang) — un même produit a N visuels ordonnés. */
  articlePhoto: (businessKey: string): string => `ph-${shortHash(businessKey)}`,
  /** Périphérique : nommé par le back-office (imprimante « Caisse 1 », TPE « Comptoir »). */
  peripheral: (kind: string, name: string): string => `per-${slug(kind)}-${slug(name)}`,
  /**
   * PHOTO DE STOCK — l'identite est le couple (magasin, variante), et elle DOIT etre frappee pareil
   * des deux cotes.
   *
   * ⚠️ POURQUOI C'EST CRITIQUE ICI. La caisse cree deja des lignes de stock quand un vendeur ajuste un
   * rayon, et le serveur en cree en consolidant les mouvements. Si les deux frappent des identifiants
   * differents, la photo descendue n'ecrase pas la ligne locale : elle tente d'en INSERER une seconde
   * pour le meme (gln, gencode) et bute sur l'index unique `ux_stock_gln_gencode`. La descente
   * echouerait ligne par ligne, en silence pour l'utilisateur.
   */
  stock: (gln: string, gencode: string): string => `stk-${gln.trim()}-${gencode.trim()}`,

  /** Commune INSEE : le couple (code postal, ville) EST l'identité — une ville peut avoir plusieurs CP. */
  inseeCity: (postalCode: string, city: string): string => `ins-${slug(postalCode)}-${slug(city)}`,
} as const;

// ── DOCUMENTS SERVEUR — mêmes identifiants des deux côtés ────────────────────────────────────────
//
// ⚠️ DEUX ÉCRIVAINS pour la même table : la RÉCEPTION du back-office (un bon saisi ou scanné) et la
// REPRISE legacy de la Console (les bons présents à la bascule). S'ils frappaient des identifiants
// différents, un bon repris puis retouché en réception existerait DEUX fois — et rien ne le dirait.
// La règle vit donc ici, dans le contrat, et les deux y délèguent.

/**
 * Le numéro d'un bon de livraison, NORMALISÉ — espaces repliés, majuscules. C'est une CLÉ, pas un texte.
 * ⚠️ Mesuré sur le legacy (audit 2026-08-23) : « bogner310326 » et « BOGNER310326 », « ICEPEAK E  » avec
 * son espace final — des bons en double qui n'en étaient qu'un. La majuscule est assumée et dite à
 * l'écran : elle rend le numéro comparable, elle ne le déforme pas.
 */
export const normalizeDeliveryNumber = (v: string): string => v.trim().replace(/\s+/g, ' ').toUpperCase();

export const documentId = {
  /** Un bon de livraison : un numéro par magasin. */
  deliveryNote: (gln: string, number: string): string => `dn:${gln.trim()}:${normalizeDeliveryNumber(number)}`,
  /** Une ligne de bon : un code-barres par bon. */
  deliveryNoteLine: (gln: string, number: string, gencode: string): string =>
    `dnl:${gln.trim()}:${normalizeDeliveryNumber(number)}:${gencode.trim()}`,
  /**
   * Une commande fournisseur : un numéro par magasin — même normalisation que le bon (D35) : la
   * réception du back-office et la reprise de la Console écrivent la même table, et le rattachement
   * BL → commande passe par ce numéro (`delivery_note.order_ref`).
   */
  supplierOrder: (gln: string, number: string): string => `so:${gln.trim()}:${normalizeDeliveryNumber(number)}`,
  /** Une ligne de commande : un code-barres par commande. */
  supplierOrderLine: (gln: string, number: string, gencode: string): string =>
    `sol:${gln.trim()}:${normalizeDeliveryNumber(number)}:${gencode.trim()}`,
  /**
   * Une rétrocession : LE MÊME identifiant dans les DEUX bases (D27) — celle de l'émetteur et celle du
   * receveur — frappé sur le magasin CRÉATEUR : sans lui, aucun suivi de bout en bout n'est possible.
   */
  retrocession: (creatorGln: string, number: string): string => `rt:${creatorGln.trim()}:${normalizeDeliveryNumber(number)}`,
  retrocessionLine: (creatorGln: string, number: string, gencode: string): string =>
    `rtl:${creatorGln.trim()}:${normalizeDeliveryNumber(number)}:${gencode.trim()}`,
  /** Un retour fournisseur : un numéro par magasin — l'annulation d'un achat, jamais un transfert (D39). */
  supplierReturn: (gln: string, number: string): string => `sr:${gln.trim()}:${normalizeDeliveryNumber(number)}`,
  supplierReturnLine: (gln: string, number: string, gencode: string): string =>
    `srl:${gln.trim()}:${normalizeDeliveryNumber(number)}:${gencode.trim()}`,
} as const;
